import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export function usePrevNextNav({ currentId, allIds, total, lastFetchParams, fetchPage, buildUrl }) {
  const navigate = useNavigate();

  const currentIndex = allIds.indexOf(currentId);
  const prevId = currentIndex > 0 ? allIds[currentIndex - 1] : null;
  const nextId = currentIndex < allIds.length - 1 ? allIds[currentIndex + 1] : null;

  const hasPrevPage = lastFetchParams && prevId === null && lastFetchParams.offset > 0;
  const hasNextPage = lastFetchParams && nextId === null && lastFetchParams.offset + lastFetchParams.limit < total;

  const [pendingDirection, setPendingDirection] = useState(null);

  const navigatePrev = useCallback(async () => {
    if (prevId !== null) {
      navigate(buildUrl(prevId));
    } else if (hasPrevPage) {
      setPendingDirection("prev");
      const newOffset = Math.max(0, lastFetchParams.offset - lastFetchParams.limit);
      await fetchPage({ ...lastFetchParams, offset: newOffset });
    }
  }, [prevId, hasPrevPage, lastFetchParams, fetchPage, navigate, buildUrl]);

  const navigateNext = useCallback(async () => {
    if (nextId !== null) {
      navigate(buildUrl(nextId));
    } else if (hasNextPage) {
      setPendingDirection("next");
      const newOffset = lastFetchParams.offset + lastFetchParams.limit;
      await fetchPage({ ...lastFetchParams, offset: newOffset });
    }
  }, [nextId, hasNextPage, lastFetchParams, fetchPage, navigate, buildUrl]);

  useEffect(() => {
    if (pendingDirection && allIds.length > 0 && !allIds.includes(currentId)) {
      const targetId = pendingDirection === "prev" ? allIds[allIds.length - 1] : allIds[0];
      setPendingDirection(null);
      navigate(buildUrl(targetId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allIds, pendingDirection]);

  useEffect(() => {
    const handleKeyNav = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowLeft" && (prevId !== null || hasPrevPage)) {
        navigatePrev();
      } else if (e.key === "ArrowRight" && (nextId !== null || hasNextPage)) {
        navigateNext();
      }
    };
    document.addEventListener("keydown", handleKeyNav);
    return () => document.removeEventListener("keydown", handleKeyNav);
  }, [prevId, nextId, hasPrevPage, hasNextPage, navigatePrev, navigateNext]);

  const visible = allIds.length > 1 || hasPrevPage || hasNextPage;

  return { prevId, nextId, hasPrevPage, hasNextPage, navigatePrev, navigateNext, visible };
}

function PrevNextNav({ currentId, allIds, total, lastFetchParams, fetchPage, buildUrl }) {
  const { t } = useTranslation();
  const { prevId, hasPrevPage, nextId, hasNextPage, navigatePrev, navigateNext, visible } = usePrevNextNav({
    currentId,
    allIds,
    total,
    lastFetchParams,
    fetchPage,
    buildUrl,
  });

  if (!visible) return null;

  return (
    <div className="btn-group">
      <button
        type="button"
        className="btn btn-white btn-sm"
        disabled={prevId === null && !hasPrevPage}
        onClick={navigatePrev}
        title={`\u2190 ${t("menu.previous")}`}
      >
        <i className="fa fa-chevron-left" />
      </button>
      <button
        type="button"
        className="btn btn-white btn-sm"
        disabled={nextId === null && !hasNextPage}
        onClick={navigateNext}
        title={`\u2192 ${t("menu.next")}`}
      >
        <i className="fa fa-chevron-right" />
      </button>
    </div>
  );
}

PrevNextNav.propTypes = {
  currentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  allIds: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  lastFetchParams: PropTypes.shape({
    offset: PropTypes.number.isRequired,
    limit: PropTypes.number.isRequired,
  }),
  fetchPage: PropTypes.func.isRequired,
  buildUrl: PropTypes.func.isRequired,
};

export default PrevNextNav;
